import type { VariableMetadata } from "@circle-react-shared/uikit/TipTap/Extensions/CustomExtensions/types";
import { createVariableMetadataMap } from "@circle-react-shared/uikit/TipTap/Extensions/CustomExtensions/Variable/constants";

import { t } from "@/i18n-js/instance";
import type { StatusBadgeProps } from "@/react/components/SettingsApp/CompassCompat/StatusBadge";

export const BROADCAST_STATUS = {
  ALL: "all",
  DRAFT: "draft",
  SENT: "sent",
  PROCESSING: "processing",
  SCHEDULED: "scheduled",
  FAILED: "failed",
  SENDING: "sending",
} as const;

export const FAILURE_REASONS = {
  TRIAL_LIMIT_REACHED: "trial_limit_reached",
  LIMIT_REACHED: "limit_reached",
  SYSTEM_UNAVAILABLE: "system_unavailable",
  EMAIL_HUB_NOT_ENABLED: "email_hub_not_enabled",
  BLOCKED: "blocked",
  UNKNOWN: "unknown",
  MAILER_ERROR: "mailer_error",
  NO_RECIPIENTS: "no_recipients",
  INVALID_SENDER_ADDRESS: "invalid_sender_address",
  INVALID_CONTENT: "invalid_content",
} as const;

export type FailureReasonValue =
  (typeof FAILURE_REASONS)[keyof typeof FAILURE_REASONS];

export const BROADCAST_TYPES = {
  standard: "standard",
  workflow: "workflow",
} as const;

export type BroadcastStatusValue =
  (typeof BROADCAST_STATUS)[keyof typeof BROADCAST_STATUS];

export const BROADCAST_TABS: ReadonlyArray<BroadcastStatusValue> = [
  BROADCAST_STATUS.ALL,
  BROADCAST_STATUS.SENT,
  BROADCAST_STATUS.SCHEDULED,
  BROADCAST_STATUS.DRAFT,
];

type BroadcastStatusVariantMapType = Record<
  (typeof BROADCAST_STATUS)[keyof typeof BROADCAST_STATUS],
  StatusBadgeProps["variant"]
>;

export const STATUS_VARIANT_MAP: BroadcastStatusVariantMapType = {
  [BROADCAST_STATUS.ALL]: "default",
  [BROADCAST_STATUS.DRAFT]: "default",
  [BROADCAST_STATUS.SENT]: "success",
  [BROADCAST_STATUS.PROCESSING]: "info",
  [BROADCAST_STATUS.SCHEDULED]: "warning",
  [BROADCAST_STATUS.FAILED]: "danger",
  [BROADCAST_STATUS.SENDING]: "info",
};

export const TAB_QUERY_NAME = "status";
export const statusForViewModal: ReadonlyArray<BroadcastStatusValue> = [
  BROADCAST_STATUS.PROCESSING,
  BROADCAST_STATUS.SENT,
  BROADCAST_STATUS.FAILED,
  BROADCAST_STATUS.SENDING,
];

export const REFETCH_INTERVAL = 15000;

const memberGroup = () => t("dynamic_vars_groups.member");
const communityGroup = () => t("dynamic_vars_groups.community");

export const BROADCAST_PERSONALIZATION_VARIABLES: VariableMetadata[] = [
  {
    get group() {
      return memberGroup();
    },
    isNullable: true,
    get label() {
      return t("dynamic_vars.member_first_name");
    },
    value: "{{member.first_name}}",
  },
  {
    get group() {
      return memberGroup();
    },
    isNullable: true,
    get label() {
      return t("dynamic_vars.member_last_name");
    },
    value: "{{member.last_name}}",
  },
  {
    get group() {
      return memberGroup();
    },
    isNullable: true,
    get label() {
      return t("dynamic_vars.member_name");
    },
    value: "{{member.name}}",
  },
  {
    get group() {
      return memberGroup();
    },
    isNullable: false,
    get label() {
      return t("dynamic_vars.member_email");
    },
    value: "{{member.email}}",
  },
  {
    get group() {
      return memberGroup();
    },
    isNullable: false,
    get label() {
      return t("dynamic_vars.member_public_uid");
    },
    value: "{{member.public_uid}}",
  },
  {
    get group() {
      return communityGroup();
    },
    isNullable: false,
    get label() {
      return t("dynamic_vars.community_name");
    },
    value: "{{community.name}}",
  },
  {
    get group() {
      return communityGroup();
    },
    isNullable: false,
    get label() {
      return t("dynamic_vars.community_url");
    },
    value: "{{community.url}}",
  },
];

export const BROADCAST_PERSONALIZATION_VARIABLES_META =
  createVariableMetadataMap(BROADCAST_PERSONALIZATION_VARIABLES);
